import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Shield, Search, Brain, Share2, FileText, Activity, Eye,
  Plus, RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Globe, Users, Briefcase, Clock, Target, Filter, Download,
  Trash2, Play, Loader2, Send, Key, Server, Smartphone,
  BookOpen, MessageSquare, Sparkles, UserCheck, UserX,
  BarChart3, PieChart, Link2, Hash, Lock, Unlock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";

const API = "/api/evet";

const STATUS_BADGE = {
  DRAFT: "bg-gray-100 text-gray-700",
  SUBMITTED: "bg-blue-100 text-blue-700",
  SCREENING: "bg-amber-100 text-amber-700",
  INVESTIGATION: "bg-purple-100 text-purple-700",
  ADJUDICATION: "bg-indigo-100 text-indigo-700",
  GRANTED: "bg-green-100 text-green-700",
  DENIED: "bg-red-100 text-red-700",
  APPEAL: "bg-orange-100 text-orange-700",
  LOW: "bg-green-100 text-green-700",
  MEDIUM: "bg-amber-100 text-amber-700",
  HIGH: "bg-red-100 text-red-700",
  SCHEDULED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-amber-100 text-amber-700",
  COMPLETED: "bg-green-100 text-green-700",
  PENDING_REVIEW: "bg-amber-100 text-amber-700",
  REVIEWED: "bg-green-100 text-green-700",
  ACTION_TAKEN: "bg-blue-100 text-blue-700",
  FALSE_ALARM: "bg-gray-100 text-gray-700",
  RESOLVED: "bg-green-100 text-green-700",
  AI_DRAFT: "bg-purple-100 text-purple-700",
  HUMAN_REVIEW: "bg-amber-100 text-amber-700",
  SENT_TO_APPLICANT: "bg-blue-100 text-blue-700",
  RESPONSE_RECEIVED: "bg-green-100 text-green-700",
};

const SEVERITY_COLORS = {
  LOW: "bg-green-500", MEDIUM: "bg-amber-500", HIGH: "bg-red-500", CRITICAL: "bg-red-700",
};

const GUIDELINES = [
  { id: 1, name: "Allegiance to the US" }, { id: 2, name: "Foreign Influence" },
  { id: 3, name: "Foreign Preference" }, { id: 4, name: "Sexual Behavior" },
  { id: 5, name: "Personal Conduct" }, { id: 6, name: "Financial Considerations" },
  { id: 7, name: "Alcohol Consumption" }, { id: 8, name: "Drug Involvement" },
  { id: 9, name: "Psychological Conditions" }, { id: 10, name: "Criminal Conduct" },
  { id: 11, name: "Handling Protected Info" }, { id: 12, name: "Outside Activities" },
  { id: 13, name: "IT Systems Misuse" },
];

// ── SA-Context Fallback Data ──
const FALLBACK_DASHBOARD = {
  total_applications: 12, in_process: 5, granted: 3, high_risk: 2,
  triage_queue_size: 4, pending_monitoring_events: 6,
};

const FALLBACK_APPLICATIONS = [
  { id: "app_001", application_number: "SAV-2026-001", status: "SCREENING", current_phase: "Phase 1" },
  { id: "app_002", application_number: "SAV-2026-002", status: "INVESTIGATION", current_phase: "Phase 2" },
  { id: "app_003", application_number: "SAV-2026-003", status: "ADJUDICATION", current_phase: "Phase 3" },
  { id: "app_004", application_number: "SAV-2026-004", status: "GRANTED", current_phase: "Phase 5" },
  { id: "app_005", application_number: "SAV-2026-005", status: "DENIED", current_phase: "Closed" },
  { id: "app_006", application_number: "SAV-2026-006", status: "SUBMITTED", current_phase: "Phase 1" },
  { id: "app_007", application_number: "SAV-2026-007", status: "SCREENING", current_phase: "Phase 1" },
  { id: "app_008", application_number: "SAV-2026-008", status: "APPEAL", current_phase: "Phase 5" },
  { id: "app_009", application_number: "SAV-2026-009", status: "INVESTIGATION", current_phase: "Phase 2" },
  { id: "app_010", application_number: "SAV-2026-010", status: "DRAFT", current_phase: "New" },
  { id: "app_011", application_number: "SAV-2026-011", status: "GRANTED", current_phase: "Monitoring" },
  { id: "app_012", application_number: "SAV-2026-012", status: "SUBMITTED", current_phase: "Phase 1" },
];

const FALLBACK_TRIAGE = [
  { id: "tri_001", application_number: "SAV-2026-001", risk_classification: "HIGH", risk_score: 78, status: "SCREENING" },
  { id: "tri_002", application_number: "SAV-2026-003", risk_classification: "MEDIUM", risk_score: 45, status: "ADJUDICATION" },
  { id: "tri_003", application_number: "SAV-2026-005", risk_classification: "HIGH", risk_score: 82, status: "DENIED" },
  { id: "tri_004", application_number: "SAV-2026-008", risk_classification: "MEDIUM", risk_score: 38, status: "APPEAL" },
];

const FALLBACK_MONITORING_EVENTS = [
  { id: "evt_001", event_type: "FOREIGN_TRAVEL", event_description: "Cleared individual travelled to Maputo, Mozambique without prior notification", risk_score_impact: 15, new_risk_score: 65, status: "PENDING_REVIEW" },
  { id: "evt_002", event_type: "CREDIT_CHANGE", event_description: "New credit facility registered with TransUnion SA", risk_score_impact: 10, new_risk_score: 55, status: "PENDING_REVIEW" },
  { id: "evt_003", event_type: "NEW_EMPLOYMENT", event_description: "Spouse started employment at foreign consulate in Pretoria", risk_score_impact: 20, new_risk_score: 72, status: "PENDING_REVIEW" },
  { id: "evt_004", event_type: "BANKRUPTCY", event_description: "Sequestration application filed in High Court, Pretoria", risk_score_impact: 35, new_risk_score: 88, status: "REVIEWED" },
  { id: "evt_005", event_type: "ARREST", event_description: "Traffic fine warrant issued in Johannesburg", risk_score_impact: 5, new_risk_score: 48, status: "FALSE_ALARM" },
  { id: "evt_006", event_type: "FOREIGN_TRAVEL", event_description: "Routine business trip to Gaborone, Botswana", risk_score_impact: -5, new_risk_score: 42, status: "REVIEWED" },
];

export default function EVETHub() {
  const { toast } = useToast();
  const location = useLocation();
  const getTabFromPath = (pathname) => {
    const seg = pathname.split("/").pop();
    if (seg === "dashboard") return "overview";
    return seg || "overview";
  };
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [loading, setLoading] = useState({});

  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [riskReport, setRiskReport] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [sor, setSor] = useState(null);
  const [monitoringEvents, setMonitoringEvents] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [triageQueue, setTriageQueue] = useState([]);
  const [hiddenLinks, setHiddenLinks] = useState([]);

  // Dialog states
  const [showNewApp, setShowNewApp] = useState(false);
  const [showInterview, setShowInterview] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [showRedact, setShowRedact] = useState(false);
  const [showRebuttal, setShowRebuttal] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(null);

  // Form states
  const [newApp, setNewApp] = useState({
    applicant_id: "", application_type: "INITIAL",
    personal_info: '{"full_name": ""}',
  });
  const [newInterview, setNewInterview] = useState({
    application_id: "", interview_type: "HUMAN_INVESTIGATOR", conducted_by: "",
  });
  const [statementInput, setStatementInput] = useState("");
  const [statementResult, setStatementResult] = useState(null);
  const [redactInput, setRedactInput] = useState({ content: "", types: "NAME,ADDRESS,PHONE,EMAIL" });
  const [redactResult, setRedactResult] = useState(null);
  const [rebuttalText, setRebuttalText] = useState("");
  const [rebuttalResult, setRebuttalResult] = useState(null);

  // Incident event form
  const [newEvent, setNewEvent] = useState({
    cleared_individual_id: "", original_application_id: "",
    event_type: "ARREST", event_description: "", source_name: "",
  });

  // ── Fetch Functions ──
  const fetchDashboard = async () => {
    try { const r = await axios.get(`${API}/dashboard`); setDashboard(r.data); } catch { setDashboard(FALLBACK_DASHBOARD); }
  };
  const fetchApps = async () => {
    try { const r = await axios.get(`${API}/applications`); setApplications(r.data.applications?.length > 0 ? r.data.applications : FALLBACK_APPLICATIONS); } catch { setApplications(FALLBACK_APPLICATIONS); }
  };
  const fetchApp = async (id) => {
    try { const r = await axios.get(`${API}/application/${id}`); setSelectedApp(r.data); } catch {}
  };
  const fetchAnomalies = async (appId) => {
    if (!appId) return;
    try { const r = await axios.get(`${API}/application/${appId}/anomalies`); setAnomalies(r.data.anomalies || []); } catch {}
  };
  const fetchRiskReport = async (appId) => {
    if (!appId) return;
    try { const r = await axios.get(`${API}/application/${appId}/risk-report`); setRiskReport(r.data); } catch {}
  };
  const fetchInterviews = async (appId) => {
    const p = appId ? `?application_id=${appId}` : "";
    try { const r = await axios.get(`${API}/interviews${p}`); setInterviews(r.data.interviews || []); } catch {}
  };
  const fetchContradictions = async (appId) => {
    if (!appId) return;
    try { const r = await axios.get(`${API}/contradictions?application_id=${appId}`); setContradictions(r.data.contradictions || []); } catch {}
  };
  const fetchGraph = async (appId) => {
    if (!appId) { setGraphData(null); return; }
    try { const r = await axios.get(`${API}/application/${appId}/graph/visualize`); setGraphData(r.data); } catch {}
  };
  const fetchSor = async (appId) => {
    if (!appId) { setSor(null); return; }
    try { const r = await axios.get(`${API}/application/${appId}/sor`); setSor(r.data); } catch { setSor(null); }
  };
  const fetchEvents = async () => {
    try { const r = await axios.get(`${API}/continuous-monitoring/events`); setMonitoringEvents(r.data.events?.length > 0 ? r.data.events : FALLBACK_MONITORING_EVENTS); } catch { setMonitoringEvents(FALLBACK_MONITORING_EVENTS); }
  };
  const fetchAudit = async (appId) => {
    if (!appId) return;
    try { const r = await axios.get(`${API}/audit/${appId}`); setAuditTrail(r.data.audit_trail || []); } catch {}
  };
  const fetchTriage = async () => {
    try { const r = await axios.get(`${API}/dashboard/triage-queue`); setTriageQueue(r.data.queue?.length > 0 ? r.data.queue : FALLBACK_TRIAGE); } catch { setTriageQueue(FALLBACK_TRIAGE); }
  };
  const fetchHiddenLinks = async (appId) => {
    if (!appId) return;
    try { const r = await axios.get(`${API}/application/${appId}/graph/hidden-links`); setHiddenLinks(r.data.hidden_links || []); } catch {}
  };

  useEffect(() => {
    fetchDashboard(); fetchApps(); fetchTriage(); fetchEvents();
  }, []);

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // ── Actions ──
  const handleCreateApp = async () => {
    try {
      const personal_info = JSON.parse(newApp.personal_info);
      await axios.post(`${API}/application/create`, {
        applicant_id: newApp.applicant_id,
        application_type: newApp.application_type,
        personal_info,
      });
      toast({ title: "Application created" });
      setShowNewApp(false); fetchApps();
    } catch (e) {
      toast({ title: JSON.stringify(e.response?.data?.detail || e.message), variant: "destructive" });
    }
  };

  const handleAnomalyScan = async (appId) => {
    try {
      setLoading(p => ({ ...p, anomaly: true }));
      const r = await axios.post(`${API}/application/${appId}/anomaly/scan`);
      toast({ title: `Anomaly scan complete: ${r.data.count || 0} found` });
      fetchAnomalies(appId);
    } catch { toast({ title: "Scan failed", variant: "destructive" }); }
    finally { setLoading(p => ({ ...p, anomaly: false })); }
  };

  const handleCalculateRisk = async (appId) => {
    try {
      setLoading(p => ({ ...p, risk: true }));
      const r = await axios.post(`${API}/application/${appId}/calculate-risk`);
      toast({ title: `Risk score: ${r.data.overall_score} (${r.data.classification})` });
      setRiskReport(r.data);
      fetchApps();
    } catch { toast({ title: "Risk calculation failed", variant: "destructive" }); }
    finally { setLoading(p => ({ ...p, risk: false })); }
  };

  const handleTriage = async (appId) => {
    try {
      const r = await axios.post(`${API}/application/${appId}/triage`);
      toast({ title: `Triaged to: ${r.data.assigned_queue}` });
      fetchApps();
    } catch { toast({ title: "Triage failed", variant: "destructive" }); }
  };

  const handleScheduleInterview = async () => {
    try {
      const r = await axios.post(`${API}/interview/schedule`, newInterview);
      toast({ title: "Interview scheduled" });
      setShowInterview(false); fetchInterviews(newInterview.application_id);
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleAnalyzeStatement = async (interviewId) => {
    if (!statementInput.trim()) return;
    try {
      setLoading(p => ({ ...p, statement: true }));
      const r = await axios.post(`${API}/interview/${interviewId}/analyze`, { statement: statementInput });
      setStatementResult(r.data);
      toast({ title: "Analysis complete" });
    } catch { toast({ title: "Analysis failed", variant: "destructive" }); }
    finally { setLoading(p => ({ ...p, statement: false })); }
  };

  const handleEndInterview = async (interviewId) => {
    try {
      const r = await axios.post(`${API}/interview/${interviewId}/end`, { statement: "Interview completed." });
      toast({ title: "Interview ended" });
      fetchInterviews();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleBuildGraph = async (appId) => {
    try {
      const r = await axios.post(`${API}/application/${appId}/build-knowledge-graph`);
      toast({ title: `Graph built: ${r.data.nodes?.length || 0} nodes, ${r.data.edges?.length || 0} edges` });
      fetchGraph(appId);
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleGnnAnalysis = async (appId) => {
    try {
      const r = await axios.post(`${API}/application/${appId}/graph/analyze-connections`);
      toast({ title: `GNN analysis: ${r.data.count || 0} hidden links` });
      fetchHiddenLinks(appId);
    } catch { toast({ title: "GNN analysis failed", variant: "destructive" }); }
  };

  const handleGenerateSor = async (appId) => {
    try {
      const r = await axios.post(`${API}/application/${appId}/generate-sor`);
      toast({ title: `SOR generated: ${r.data.recommendation}` });
      fetchSor(appId);
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRedact = async () => {
    try {
      const types = redactInput.types.split(",").map(t => t.trim()).filter(Boolean);
      const r = await axios.post(`${API}/sor/redact`, { content: redactInput.content, types });
      setRedactResult(r.data);
      toast({ title: `Redacted: ${r.data.count || 0} items` });
    } catch { toast({ title: "Redaction failed", variant: "destructive" }); }
  };

  const handleAnalyzeRebuttal = async (appId) => {
    if (!rebuttalText.trim()) return;
    try {
      const r = await axios.post(`${API}/application/${appId}/rebuttal/analyze`, { rebuttal_text: rebuttalText });
      setRebuttalResult(r.data);
      toast({ title: "Rebuttal analyzed" });
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateEvent = async () => {
    try {
      const r = await axios.post(`${API}/continuous-monitoring/events`, newEvent);
      toast({ title: `Event processed: score ${r.data.previous_score} → ${r.data.new_score}` });
      fetchEvents();
      setNewEvent({ cleared_individual_id: "", original_application_id: "", event_type: "ARREST", event_description: "", source_name: "" });
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleReviewEvent = async (eventId, status, notes) => {
    try {
      await axios.post(`${API}/continuous-monitoring/events/${eventId}/review`, { status, notes, action_taken: status === "FALSE_ALARM" ? "NO_ACTION" : "REVIEWED" });
      toast({ title: "Event reviewed" }); fetchEvents();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleEnroll = async (memberId, appId) => {
    try {
      await axios.post(`${API}/continuous-monitoring/enroll/${memberId}?application_id=${appId}`);
      toast({ title: "Enrolled in continuous monitoring" });
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const appSelector = (
    <Select value={selectedApp?.id || "_none"} onValueChange={v => {
      if (v === "_none") { setSelectedApp(null); setAnomalies([]); setRiskReport(null); setSor(null); setGraphData(null); setHiddenLinks([]); setAuditTrail([]); return; }
      const app = applications.find(a => a.id === v);
      setSelectedApp(app);
      fetchApp(v);
      fetchAnomalies(v);
      fetchRiskReport(v);
      fetchInterviews(v);
      fetchContradictions(v);
      fetchGraph(v);
      fetchSor(v);
      fetchHiddenLinks(v);
      fetchAudit(v);
    }}>
      <SelectTrigger className="w-56"><SelectValue placeholder="Select application..." /></SelectTrigger>
      <SelectContent>
        <SelectItem value="_none">Select application...</SelectItem>
        {applications.map(a => (
          <SelectItem key={a.id} value={a.id}>
            {a.application_number} — {a.status}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const kpiCards = dashboard ? [
    { title: "Applications", value: dashboard.total_applications || 0, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50" },
    { title: "In Process", value: dashboard.in_process || 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { title: "Granted", value: dashboard.granted || 0, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
    { title: "High Risk", value: dashboard.high_risk || 0, icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
    { title: "Triage Queue", value: dashboard.triage_queue_size || 0, icon: Target, color: "text-purple-600", bg: "bg-purple-50" },
    { title: "Pending Events", value: dashboard.pending_monitoring_events || 0, icon: Activity, color: "text-blue-600", bg: "bg-blue-50" },
  ] : [];

  const overviewContent = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map(k => (
          <Card key={k.title} className={k.bg}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{k.title}</p>
                  <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
                </div>
                <k.icon className={`h-8 w-8 ${k.color} opacity-70`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle>Triage Queue</CardTitle></CardHeader>
          <CardContent>
            {triageQueue.length === 0 ? <p className="text-sm text-muted-foreground">No items in queue</p> : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {triageQueue.slice(0, 10).map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2 hover:bg-app rounded">
                    <div className="flex items-center gap-2">
                      <Badge className={STATUS_BADGE[a.risk_classification] || ""}>{a.risk_classification || "N/A"}</Badge>
                      <span className="text-sm font-medium">{a.application_number}</span>
                    </div>
                    <Badge className={STATUS_BADGE[a.status]}>{a.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card><CardHeader><CardTitle>Recent Monitoring Events</CardTitle></CardHeader>
          <CardContent>
            {monitoringEvents.length === 0 ? <p className="text-sm text-muted-foreground">No events</p> : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {monitoringEvents.slice(0, 5).map(e => (
                  <div key={e.id} className="flex items-center justify-between p-2 hover:bg-app rounded">
                    <div>
                      <Badge variant="outline">{e.event_type}</Badge>
                      <span className="text-xs ml-2 text-muted-foreground">{e.event_description?.slice(0, 40)}</span>
                    </div>
                    <Badge className={STATUS_BADGE[e.status]}>{e.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
            <span>Quick Actions:</span>
            <Button variant="outline" size="sm" onClick={() => setShowNewApp(true)}>
              <Plus className="h-3 w-3 mr-1" /> New Application
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab("screener")}>
              <BarChart3 className="h-3 w-3 mr-1" /> Risk Scoring
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab("interrogator")}>
              <Brain className="h-3 w-3 mr-1" /> Interview Co-Pilot
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab("sentinel")}>
              <Activity className="h-3 w-3 mr-1" /> Monitoring
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">eVET Hub</h1>
          <p className="text-muted-foreground">AI-Powered Vetting & Clearance Platform</p>
        </div>
        <div className="flex items-center gap-2">
          {appSelector}
          <Button variant="outline" size="sm" onClick={() => { fetchDashboard(); fetchApps(); fetchTriage(); fetchEvents(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="collector">Collector</TabsTrigger>
          <TabsTrigger value="screener">Screener</TabsTrigger>
          <TabsTrigger value="interrogator">Interrogator</TabsTrigger>
          <TabsTrigger value="connector">Connector</TabsTrigger>
          <TabsTrigger value="clerk">Clerk</TabsTrigger>
          <TabsTrigger value="sentinel">Sentinel</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* ═══ OVERVIEW ═══ */}
        <TabsContent value="overview">{overviewContent}</TabsContent>

        {/* ═══ PHASE 1: COLLECTOR ═══ */}
        <TabsContent value="collector" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" /> Phase 1: Collector
              </CardTitle>
              <Dialog open={showNewApp} onOpenChange={setShowNewApp}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> New Application</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create Vetting Application</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Applicant ID</Label><Input value={newApp.applicant_id} onChange={e => setNewApp({...newApp, applicant_id: e.target.value})} /></div>
                    <div><Label>Type</Label>
                      <Select value={newApp.application_type} onValueChange={v => setNewApp({...newApp, application_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="INITIAL">Initial</SelectItem>
                          <SelectItem value="RENEWAL">Renewal</SelectItem>
                          <SelectItem value="UPGRADE">Upgrade</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Personal Info (JSON)</Label><Textarea value={newApp.personal_info} onChange={e => setNewApp({...newApp, personal_info: e.target.value})} rows={3} /></div>
                    <Button onClick={handleCreateApp}>Create Application</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
          </Card>

          {selectedApp && (
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <Badge className={STATUS_BADGE[selectedApp.status]}>{selectedApp.status}</Badge>
                <span className="text-sm font-medium">{selectedApp.application_number}</span>
                <span className="text-sm text-muted-foreground">Phase: {selectedApp.current_phase}</span>
                <Button variant="outline" size="sm" onClick={() => handleAnomalyScan(selectedApp.id)} disabled={loading.anomaly}>
                  {loading.anomaly ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <AlertTriangle className="h-3 w-3 mr-1" />}Scan Anomalies
                </Button>
              </div>
            </CardContent></Card>
          )}

          {anomalies.length > 0 && (
            <Card><CardHeader><CardTitle>Anomalies ({anomalies.length})</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {anomalies.map(a => (
                      <TableRow key={a.id}>
                        <TableCell><Badge variant="outline">{a.anomaly_type}</Badge></TableCell>
                        <TableCell><Badge className={STATUS_BADGE[a.severity]}>{a.severity}</Badge></TableCell>
                        <TableCell className="text-sm max-w-md">{a.description}</TableCell>
                        <TableCell>{a.resolved ? <Badge className="bg-green-100 text-green-700">Resolved</Badge> : <Badge className="bg-amber-100 text-amber-700">Open</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ PHASE 2: SCREENER ═══ */}
        <TabsContent value="screener" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Phase 2: Screener — Risk Scoring & Triage</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">Select an application above, then calculate risk or triage.</p>
              {selectedApp && (
                <div className="flex gap-2 mb-4">
                  <Button onClick={() => handleCalculateRisk(selectedApp.id)} disabled={loading.risk}>
                    {loading.risk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...</> : <><BarChart3 className="h-4 w-4 mr-2" /> Calculate Risk</>}
                  </Button>
                  <Button variant="outline" onClick={() => handleTriage(selectedApp.id)}>
                    <Target className="h-4 w-4 mr-2" /> Triage Case
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {riskReport && (
            <Card><CardHeader><CardTitle>Risk Report — Score: {riskReport.overall_score} / 100 ({riskReport.classification})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {riskReport.guideline_scores?.map(gs => (
                    <div key={gs.guideline_id} className="flex items-center gap-3 p-2 hover:bg-app rounded">
                      <Badge variant="outline" className="w-32 shrink-0">{gs.guideline_name}</Badge>
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className={`h-2 rounded-full ${gs.score >= 50 ? "bg-red-500" : gs.score >= 20 ? "bg-amber-500" : "bg-green-500"}`}
                          style={{ width: `${gs.score}%` }} />
                      </div>
                      <span className="text-sm font-mono w-8 text-right">{gs.score}</span>
                      <span className="text-xs text-muted-foreground max-w-xs truncate">{gs.explainable_reasoning}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card><CardHeader><CardTitle>Triage Queue ({triageQueue.length})</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>App #</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {triageQueue.map(a => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">{a.application_number}</TableCell>
                      <TableCell>{a.risk_score || 0}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[a.risk_classification]}>{a.risk_classification}</Badge></TableCell>
                      <TableCell><Badge className={STATUS_BADGE[a.status]}>{a.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ PHASE 3: INTERROGATOR ═══ */}
        <TabsContent value="interrogator" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Brain className="h-5 w-5" /> Phase 3: Interrogator — Interview Co-Pilot</CardTitle>
              <Dialog open={showInterview} onOpenChange={setShowInterview}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" /> Schedule Interview</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Schedule Interview</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Application ID</Label><Input value={newInterview.application_id} onChange={e => setNewInterview({...newInterview, application_id: e.target.value})} /></div>
                    <div><Label>Type</Label>
                      <Select value={newInterview.interview_type} onValueChange={v => setNewInterview({...newInterview, interview_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PRELIMINARY_AI">Preliminary AI</SelectItem>
                          <SelectItem value="HUMAN_INVESTIGATOR">Human Investigator</SelectItem>
                          <SelectItem value="APPEAL">Appeal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Conducted By</Label><Input value={newInterview.conducted_by} onChange={e => setNewInterview({...newInterview, conducted_by: e.target.value})} /></div>
                    <Button onClick={handleScheduleInterview}>Schedule</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
          </Card>

          {/* Statement Analyzer */}
          <Card><CardHeader><CardTitle>Real-time Statement Analysis</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input value={statementInput} onChange={e => setStatementInput(e.target.value)}
                  placeholder="Enter applicant's statement to analyze..." className="flex-1" />
                <Button onClick={() => handleAnalyzeStatement(interviews[0]?.id)} disabled={loading.statement || !statementInput}>
                  {loading.statement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />} Analyze
                </Button>
              </div>

              {statementResult && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div>
                    <h4 className="font-semibold text-sm flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-red-500" /> Contradictions</h4>
                    {statementResult.contradictions?.contradictions?.length === 0 ? (
                      <p className="text-sm text-green-600">No contradictions detected</p>
                    ) : (
                      statementResult.contradictions?.contradictions?.map((c, i) => (
                        <div key={i} className="text-sm p-2 bg-red-50 rounded mt-1">
                          <Badge className={STATUS_BADGE[c.severity]}>{c.severity}</Badge>
                          <p className="mt-1">{c.explanation}</p>
                          <p className="text-xs text-muted-foreground mt-1">Source: {c.conflicting_source}</p>
                        </div>
                      ))
                    )}
                  </div>
                  {statementResult.sentiment && (
                    <div>
                      <h4 className="font-semibold text-sm">Sentiment Analysis <span className="text-xs text-amber-600 font-normal">(AI SUGGESTION — NOT EVIDENCE)</span></h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                        <div className="p-2 bg-app rounded text-center">
                          <p className="text-xs text-muted-foreground">Stress</p>
                          <p className="font-bold">{statementResult.sentiment.stress_score}/100</p>
                        </div>
                        <div className="p-2 bg-app rounded text-center">
                          <p className="text-xs text-muted-foreground">Evasion</p>
                          <p className="font-bold">{statementResult.sentiment.evasion_score}/100</p>
                        </div>
                        <div className="p-2 bg-app rounded text-center">
                          <p className="text-xs text-muted-foreground">Deception Indicators</p>
                          <p className="font-bold">{statementResult.sentiment.deception_indicators}/100</p>
                        </div>
                        <div className="p-2 bg-app rounded text-center">
                          <p className="text-xs text-muted-foreground">Hesitations</p>
                          <p className="font-bold">{statementResult.sentiment.hesitation_count}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {statementResult.suggested_questions?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm">Suggested Follow-up Questions</h4>
                      <ul className="list-disc list-inside text-sm space-y-1 mt-1">
                        {statementResult.suggested_questions.map((q, i) => (
                          <li key={i} className="text-muted-foreground">{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {interviews.length > 0 && (
            <Card><CardHeader><CardTitle>Interviews ({interviews.length})</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {interviews.map(i => (
                      <TableRow key={i.id}>
                        <TableCell><Badge variant="outline">{i.interview_type}</Badge></TableCell>
                        <TableCell><Badge className={STATUS_BADGE[i.status]}>{i.status}</Badge></TableCell>
                        <TableCell className="text-xs">{i.started_at ? new Date(i.started_at).toLocaleString() : "-"}</TableCell>
                        <TableCell>
                          {i.status === "IN_PROGRESS" && <Button variant="ghost" size="sm" onClick={() => handleEndInterview(i.id)}><XCircle className="h-3 w-3 mr-1" />End</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ PHASE 4: CONNECTOR ═══ */}
        <TabsContent value="connector" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Share2 className="h-5 w-5" /> Phase 4: Connector — Knowledge Graph & GNN</CardTitle></CardHeader>
            <CardContent>
              {selectedApp ? (
                <div className="flex gap-2 mb-4">
                  <Button onClick={() => handleBuildGraph(selectedApp.id)}>
                    <Share2 className="h-4 w-4 mr-2" /> Build Knowledge Graph
                  </Button>
                  <Button variant="outline" onClick={() => handleGnnAnalysis(selectedApp.id)}>
                    <Brain className="h-4 w-4 mr-2" /> GNN Hidden Link Analysis
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select an application from the dropdown above.</p>
              )}
            </CardContent>
          </Card>

          {graphData && (
            <Card><CardHeader><CardTitle>Knowledge Graph — {graphData.nodes?.length || 0} nodes, {graphData.edges?.length || 0} edges</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {[...new Set((graphData.nodes || []).map(n => n.node_type))].map(type => (
                    <div key={type} className="p-2 bg-app rounded text-center">
                      <p className="text-xs text-muted-foreground">{type}</p>
                      <p className="font-bold">{(graphData.nodes || []).filter(n => n.node_type === type).length}</p>
                    </div>
                  ))}
                </div>
                <div className="border rounded p-3 max-h-48 overflow-y-auto text-xs font-mono">
                  {(graphData.edges || []).slice(0, 20).map(e => (
                    <div key={e.id} className="py-0.5">
                      {e.source_node_id?.slice(0, 12)} —[{e.relationship_type}]→ {e.target_node_id?.slice(0, 12)}
                      <span className="text-muted-foreground ml-2">strength: {e.strength || 0}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {hiddenLinks.length > 0 && (
            <Card><CardHeader><CardTitle>Hidden Links Discovered by GNN ({hiddenLinks.length})</CardTitle></CardHeader>
              <CardContent>
                {hiddenLinks.map((h, i) => (
                  <div key={i} className="p-3 border rounded mb-2 bg-purple-50">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-purple-100">GNN DISCOVERY</Badge>
                      <span className="text-sm">Confidence: {Math.round(h.discovery_confidence || 0)}%</span>
                    </div>
                    <p className="text-sm mt-1">{h.evidence_sources?.join(" → ")}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ PHASE 5: CLERK ═══ */}
        <TabsContent value="clerk" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Phase 5: Clerk — SOR & Redaction</CardTitle></CardHeader>
            <CardContent>
              {selectedApp ? (
                <div className="flex gap-2 mb-4">
                  <Button onClick={() => handleGenerateSor(selectedApp.id)}>
                    <FileText className="h-4 w-4 mr-2" /> Generate Statement of Reasons
                  </Button>
                </div>
              ) : <p className="text-sm text-muted-foreground">Select an application above.</p>}
            </CardContent>
          </Card>

          {sor && (
            <Card><CardHeader><CardTitle>SOR: {sor.sor_number} — {sor.recommendation}</CardTitle></CardHeader>
              <CardContent>
                <Badge className={STATUS_BADGE[sor.status]}>{sor.status}</Badge>
                <pre className="mt-3 text-sm bg-app p-4 rounded max-h-80 overflow-y-auto whitespace-pre-wrap font-sans">{sor.ai_drafted_content?.slice(0, 2000)}</pre>
              </CardContent>
            </Card>
          )}

          {/* Redaction Tool */}
          <Card><CardHeader><CardTitle>Redaction Assistant</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div><Label>Content to Redact</Label><Textarea value={redactInput.content} onChange={e => setRedactInput({...redactInput, content: e.target.value})} rows={4} /></div>
                <div><Label>Types (comma-separated)</Label><Input value={redactInput.types} onChange={e => setRedactInput({...redactInput, types: e.target.value})} /></div>
                <Button onClick={handleRedact}><Lock className="h-4 w-4 mr-2" /> Apply Redaction</Button>
              </div>
              {redactResult && (
                <div className="mt-4 border rounded p-4">
                  <p className="text-sm font-semibold">{redactResult.count} redactions applied</p>
                  <pre className="text-sm bg-app p-3 rounded mt-2 max-h-48 overflow-y-auto">{redactResult.redacted_content}</pre>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rebuttal Analysis */}
          <Card><CardHeader><CardTitle>Rebuttal Analyzer</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div><Label>Applicant Rebuttal Text</Label><Textarea value={rebuttalText} onChange={e => setRebuttalText(e.target.value)} rows={4} placeholder="Paste applicant's rebuttal here..." /></div>
                <Button onClick={() => selectedApp && handleAnalyzeRebuttal(selectedApp.id)}>
                  <Brain className="h-4 w-4 mr-2" /> Analyze Rebuttal
                </Button>
              </div>
              {rebuttalResult && (
                <div className="mt-4 border rounded p-4 space-y-2">
                  <p className="text-sm">Original Score: <strong>{rebuttalResult.original_risk_score}</strong></p>
                  <p className="text-sm">Revised Score: <strong>{rebuttalResult.revised_risk_score}</strong></p>
                  <p className="text-sm">Delta: <span className={rebuttalResult.score_delta <= 0 ? "text-green-600" : "text-red-600"}>{rebuttalResult.score_delta}</span></p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ PHASE 6: SENTINEL ═══ */}
        <TabsContent value="sentinel" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Activity className="h-5 w-5" /> Phase 6: Sentinel — Continuous Monitoring</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1"><Input placeholder="Member ID" value={newEvent.cleared_individual_id} onChange={e => setNewEvent({...newEvent, cleared_individual_id: e.target.value})} /></div>
                  <div className="flex-1"><Input placeholder="Application ID" value={newEvent.original_application_id} onChange={e => setNewEvent({...newEvent, original_application_id: e.target.value})} /></div>
                  <Select value={newEvent.event_type} onValueChange={v => setNewEvent({...newEvent, event_type: v})}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARREST">Arrest</SelectItem>
                      <SelectItem value="BANKRUPTCY">Bankruptcy</SelectItem>
                      <SelectItem value="FOREIGN_TRAVEL">Foreign Travel</SelectItem>
                      <SelectItem value="NEW_EMPLOYMENT">New Employment</SelectItem>
                      <SelectItem value="CREDIT_CHANGE">Credit Change</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={handleCreateEvent}><Play className="h-4 w-4 mr-2" /> Process Event</Button>
                </div>
                <div><Input placeholder="Event description" value={newEvent.event_description} onChange={e => setNewEvent({...newEvent, event_description: e.target.value})} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Monitoring Events ({monitoringEvents.length})</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEnroll("new_member", "sample_app")}>
                  <UserCheck className="h-3 w-3 mr-1" /> Enroll Test
                </Button>
                <Button variant="outline" size="sm" onClick={fetchEvents}><RefreshCw className="h-3 w-3" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Impact</TableHead>
                  <TableHead>New Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {monitoringEvents.map(e => (
                    <TableRow key={e.id}>
                      <TableCell><Badge variant="outline">{e.event_type}</Badge></TableCell>
                      <TableCell className="max-w-xs truncate text-sm">{e.event_description || "-"}</TableCell>
                      <TableCell><span className={e.risk_score_impact > 0 ? "text-red-600 font-medium" : "text-green-600"}>{e.risk_score_impact > 0 ? `+${e.risk_score_impact}` : e.risk_score_impact}</span></TableCell>
                      <TableCell className="font-mono">{e.new_risk_score}</TableCell>
                      <TableCell><Badge className={STATUS_BADGE[e.status]}>{e.status}</Badge></TableCell>
                      <TableCell>
                        {e.status === "PENDING_REVIEW" && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleReviewEvent(e.id, "REVIEWED", "Reviewed — no action needed")}><CheckCircle className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => handleReviewEvent(e.id, "FALSE_ALARM", "False alarm")}><XCircle className="h-3 w-3" /></Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ AUDIT ═══ */}
        <TabsContent value="audit" className="space-y-4 pt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5" /> Audit Trail & Guardrails</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 border rounded-lg bg-blue-50">
                  <h4 className="font-semibold text-sm flex items-center gap-1"><Shield className="h-4 w-4 text-blue-600" /> No Fully Automated Denials</h4>
                  <p className="text-sm text-muted-foreground mt-1">Human adjudicator must approve all GRANT/DENY decisions.</p>
                </div>
                <div className="p-4 border rounded-lg bg-green-50">
                  <h4 className="font-semibold text-sm flex items-center gap-1"><BarChart3 className="h-4 w-4 text-green-600" /> Bias Auditing</h4>
                  <p className="text-sm text-muted-foreground mt-1">Regular demographic parity checks via built-in audit endpoint.</p>
                </div>
                <div className="p-4 border rounded-lg bg-purple-50">
                  <h4 className="font-semibold text-sm flex items-center gap-1"><FileText className="h-4 w-4 text-purple-600" /> Right to Explanation</h4>
                  <p className="text-sm text-muted-foreground mt-1">Applicants can request explanation of AI-driven risk scores.</p>
                </div>
                <div className="p-4 border rounded-lg bg-amber-50">
                  <h4 className="font-semibold text-sm flex items-center gap-1"><Lock className="h-4 w-4 text-amber-600" /> Adversarial Protection</h4>
                  <p className="text-sm text-muted-foreground mt-1">Input sanitization and prompt injection detection.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedApp && (
            <Card><CardHeader><CardTitle>Audit Trail for {selectedApp.application_number}</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Action</TableHead>
                    <TableHead>Phase</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {auditTrail.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-sm text-muted-foreground">No audit entries yet</TableCell></TableRow>
                    ) : auditTrail.map(a => (
                      <TableRow key={a.id || Math.random()}>
                        <TableCell><Badge variant="outline">{a.action}</Badge></TableCell>
                        <TableCell>{a.phase || "-"}</TableCell>
                        <TableCell className="text-xs">{a.action_by?.slice(0, 12) || "-"}</TableCell>
                        <TableCell className="text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
